import { useEffect, useMemo } from "react";
import { useParams, useNavigate, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { LiveChatTeaser, FloatingWhatsApp } from "@/components/LiveChatTeaser";
import { Button } from "@/components/ui/button";
import { useSEO } from "@/hooks/useSEO";
import { getServiceBySlug, SERVICES, PROCESS_STEPS, SITE_URL, CONTACT_INFO } from "@/data/content";
import { CheckCircle2, ChevronRight, ArrowRight, Star } from "lucide-react";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.07 } },
};
const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.21, 0.61, 0.35, 1] } },
};

export default function ServicePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const service = getServiceBySlug(slug);

  const jsonLd = useMemo(
    () =>
      service
        ? {
            "@context": "https://schema.org",
            "@type": "Service",
            name: service.name,
            description: service.seoDescription,
            url: `${SITE_URL}/${service.slug}`,
            provider: {
              "@type": "Organization",
              name: "GrowthSync Solutions",
              url: SITE_URL,
              email: CONTACT_INFO.email,
              telephone: CONTACT_INFO.phoneRaw,
              address: {
                "@type": "PostalAddress",
                addressLocality: "Dhaka",
                addressCountry: "BD",
              },
            },
            areaServed: ["Worldwide"],
          }
        : null,
    [service]
  );

  useSEO({
    title: service?.seoTitle,
    description: service?.seoDescription,
    canonical: service ? `${SITE_URL}/${service.slug}` : undefined,
    jsonLd,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!service) {
    return <Navigate to="/" replace />;
  }

  const goToQuote = () => {
    sessionStorage.setItem("gs-preselect-service", service.name);
    navigate("/", { state: { scrollTo: "#contact", service: service.name } });
  };

  const otherServices = SERVICES.filter((s) => s.slug !== slug);
  const Icon = service.icon;

  return (
    <div className="relative min-h-screen" style={{ backgroundColor: "var(--gs-navy-900)" }} data-testid="service-page">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="hero-overlay" />
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="relative mx-auto max-w-[var(--container-max)] px-4 pb-14 pt-10 sm:px-6 sm:pt-14"
          >
            {/* Breadcrumb */}
            <motion.nav variants={item} aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1.5 text-sm text-white/50" data-testid="service-breadcrumb">
              <Link to="/" className="hover:text-[color:var(--gs-gold-400)]">Home</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <Link to="/" state={{ scrollTo: "#services" }} className="hover:text-[color:var(--gs-gold-400)]">Services</Link>
              <ChevronRight className="h-3.5 w-3.5" />
              <span className="text-white/85">{service.name}</span>
            </motion.nav>

            <motion.div variants={item} className="mt-9 flex h-16 w-16 items-center justify-center rounded-2xl border border-[color:var(--gs-line)] bg-[rgba(240,180,41,0.1)]">
              <Icon className="h-8 w-8 text-[color:var(--gs-gold-400)]" />
            </motion.div>

            <motion.h1
              variants={item}
              data-testid="service-page-title"
              className="font-display mt-6 max-w-3xl text-4xl font-bold text-white sm:text-5xl"
            >
              {service.name}
            </motion.h1>
            <motion.p variants={item} className="mt-4 max-w-2xl text-lg text-[color:var(--gs-gold-400)]">
              {service.tagline}
            </motion.p>

            <motion.div variants={item} className="mt-6 max-w-3xl space-y-4">
              {service.longDescription.map((para, i) => (
                <p key={i} className="leading-relaxed text-white/70">{para}</p>
              ))}
            </motion.div>

            <motion.div variants={item} className="mt-9 flex flex-wrap gap-4">
              <Button
                size="lg"
                onClick={goToQuote}
                data-testid="service-page-quote-button"
                className="h-12 bg-[color:var(--gs-gold-400)] px-8 text-base font-semibold text-[color:var(--gs-navy-950)] hover:bg-[color:var(--gs-gold-500)] active:scale-[0.98]"
              >
                Request a Quote <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                asChild
                className="h-12 border-white/15 bg-white/5 px-8 text-base text-white hover:border-[color:var(--gs-line)] hover:bg-white/10 hover:text-white"
              >
                <Link to="/" state={{ scrollTo: "#services" }} data-testid="service-page-all-services-link">
                  View All Services
                </Link>
              </Button>
            </motion.div>
          </motion.div>
        </section>

        {/* What's included */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="lg:col-span-5">
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--gs-gold-400)]">
                  What's Included
                </p>
                <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl">
                  Everything you get with our {service.name.toLowerCase()} service
                </h2>
                <div className="mt-4 h-1 w-16 rounded bg-[color:var(--gs-gold-400)]" />
              </div>
              <div className="lg:col-span-7">
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {service.features.map((feature) => (
                    <li
                      key={feature}
                      data-testid="service-feature-item"
                      className="flex items-start gap-3 rounded-xl border border-white/10 bg-[color:var(--gs-glass)] p-4 text-sm text-white/85 backdrop-blur"
                    >
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--gs-gold-400)]" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
            <div className="mb-10 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--gs-gold-400)]">
                Why It Works
              </p>
              <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl">
                Benefits you can measure
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {service.benefits.map((benefit) => (
                <div
                  key={benefit}
                  data-testid="service-benefit-card"
                  className="rounded-[var(--radius)] border border-white/10 bg-[color:var(--gs-glass)] p-6 backdrop-blur hover:border-[color:var(--gs-line)]"
                >
                  <Star className="h-5 w-5 text-[color:var(--gs-gold-400)]" />
                  <p className="mt-3 text-sm font-medium leading-relaxed text-white/85">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
            <div className="mb-10 text-center">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[color:var(--gs-gold-400)]">
                How We Work
              </p>
              <h2 className="font-display mt-3 text-2xl font-bold text-white sm:text-3xl">
                From first call to first results
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {PROCESS_STEPS.map((step) => (
                <div
                  key={step.step}
                  className="relative rounded-[var(--radius)] border border-white/10 bg-[color:var(--gs-glass)] p-6 backdrop-blur"
                >
                  <span className="font-display text-3xl font-bold text-[color:var(--gs-gold-400)]/30">
                    {step.step}
                  </span>
                  <h3 className="font-display mt-2 text-base font-semibold text-white">{step.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-white/60">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="py-12 sm:py-16">
          <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
            <div className="rounded-[var(--radius)] border border-[color:var(--gs-line)] bg-[color:var(--gs-glass)] p-8 text-center shadow-[var(--shadow-gold)] backdrop-blur sm:p-12">
              <h2 className="font-display text-2xl font-bold text-white sm:text-3xl">
                Ready to get started with{" "}
                <span className="text-[color:var(--gs-gold-400)]">{service.name}</span>?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-white/60">
                Get a free, tailored quotation within 24 hours — no commitment required.
              </p>
              <Button
                size="lg"
                onClick={goToQuote}
                data-testid="service-page-cta-button"
                className="mt-7 h-12 bg-[color:var(--gs-gold-400)] px-10 text-base font-semibold text-[color:var(--gs-navy-950)] hover:bg-[color:var(--gs-gold-500)] active:scale-[0.98]"
              >
                Request Free Quotation
              </Button>
            </div>
          </div>
        </section>

        {/* Other services - internal links for SEO */}
        <section className="pb-16 pt-4">
          <div className="mx-auto max-w-[var(--container-max)] px-4 sm:px-6">
            <h3 className="font-display text-lg font-semibold text-white">Explore other services</h3>
            <div className="mt-4 flex flex-wrap gap-2.5">
              {otherServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/${s.slug}`}
                  data-testid="related-service-link"
                  className="rounded-full border border-white/15 bg-white/5 px-4 py-2 text-sm text-white/70 hover:border-[color:var(--gs-line)] hover:text-[color:var(--gs-gold-400)]"
                >
                  {s.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
      <LiveChatTeaser />
    </div>
  );
}
